/**
 * Контракты публичного каталога (MVP_TECH_RFC §21).
 *
 * Зеркало backend-схем `VehicleCardOut` и `VehiclePageOut` (BE-050). Пока в
 * проекте нет генерации типов из OpenAPI (`npm run api:generate` появится вместе
 * с первой мутирующей фичей), контракт описан вручную и обязан совпадать с
 * `backend/app/modules/vehicles/schemas.py` — расхождение ловит E2E на реальном стеке.
 *
 * Два правила, которые нельзя «упростить»:
 *
 * * отсутствующее значение приходит как `null` и показывается как «Нет данных»;
 *   ноль и пустая строка сюда не подставляются (MVP_PRD §18);
 * * **«не считали» и «посчитали, ничего не нашли» — разные ответы.** `market = null`
 *   и `attention_flags = null` означают, что аналитика для автомобиля не
 *   рассчитывалась; посчитанное отсутствие сравнения приходит статусом
 *   `insufficient_data` внутри объекта, а пустой список признаков — это `[]`.
 *   `attention_count = 0` **не означает** «замечаний нет»: такого вывода система
 *   делать не имеет права (MVP_PRD §15). `match` остаётся `null` до Epic 8.
 */

/** MVP_TECH_RFC §8: набор совпадает с `fuel.allowed` в Criteria Document. */
export type FuelType =
  'petrol' | 'diesel' | 'mild_hybrid' | 'full_hybrid' | 'plug_in_hybrid' | 'electric';

export type Transmission = 'manual' | 'automatic';

export type Drivetrain = 'fwd' | 'rwd' | 'awd';

/** `suv` покрывает и кроссоверы: в критериях это один вариант (MVP_PRD §10). */
export type BodyType = 'hatchback' | 'sedan' | 'wagon' | 'suv' | 'minivan' | 'coupe' | 'cabrio';

/**
 * Allowlist сортировок публичного каталога (MVP_TECH_RFC §21).
 * `match_desc` и `confidence_desc` требуют выполненного подбора, поэтому здесь их нет.
 */
export type CatalogSort =
  'newest' | 'price_asc' | 'price_desc' | 'registration_desc' | 'mileage_asc';

export interface VehiclePrice {
  amount_cents: number;
  currency: string;
  /** Строка, собранная сервером («28 900 €»). Запасной вариант, если Intl не знает валюту. */
  formatted: string;
}

export interface VehicleLocation {
  /** `null` — источник не указал город. Показывается как «Нет данных». */
  city: string | null;
  /** Федеральная земля, ISO 3166-2:DE без префикса, например `BE`. `null` — неизвестна. */
  state_code: string | null;
  country_code: string;
}

export interface VehicleCoverImage {
  /** Путь готового варианта под Nginx (`/media/vehicles/...`), либо `null`. */
  card_url: string | null;
  alt: string | null;
}

// --- Рыночное сравнение и признаки внимания (Epic 6) ----------------------------------

/**
 * Статус сравнения с медианой (MVP_TECH_RFC §14).
 *
 * `available` — снимок не старше 30 дней, числа показываются как есть;
 * `stale` — снимку 31–90 дней, числа показываются с оговоркой;
 * `unavailable` — подходящие выборки есть, но все старше 90 дней, и тогда
 * **числовое сравнение не показывается вовсе** (MVP_PRD §16);
 * `insufficient_data` — сопоставимой выборки нет.
 */
export type MarketEvaluationStatus = 'available' | 'stale' | 'unavailable' | 'insufficient_data';

/** Уровень совпадения выборки (MVP_PRD §16): A — самый узкий, C — самый широкий. */
export type BenchmarkMatchLevel = 'A' | 'B' | 'C';

/**
 * Сравнение цены с рыночной медианой (MVP_PRD §16).
 *
 * Числовые поля объявлены `null` вместе, а не по отдельности: backend не отдаёт
 * медиану без размера выборки и даты снимка (CHECK в схеме), поэтому «число без
 * основания» на экран попасть не может.
 */
export interface MarketComparisonData {
  status: MarketEvaluationStatus;
  match_level: BenchmarkMatchLevel | null;
  median_price: VehiclePrice | null;
  delta_cents: number | null;
  /** Отклонение в процентах со знаком: отрицательное — цена ниже медианы. */
  delta_percent: number | null;
  sample_size: number | null;
  /** Дата снимка рыночной выборки, `YYYY-MM-DD`. */
  snapshot_date: string | null;
  /** Снимку 31–90 дней: числа показываются с предупреждением. */
  is_stale: boolean;
  /** Качество сравнения 0–100 (уровень, размер выборки, свежесть). Не вероятность. */
  confidence_score: number;
  calculated_at: string;
}

/** Уровни признаков (MVP_PRD §15). Уровня «критический технический риск» нет. */
export type AttentionSeverity = 'info' | 'requires_attention' | 'significant_uncertainty';

/**
 * Признак, требующий внимания (MVP_PRD §15).
 *
 * Наружу приходит **код**, тексты живут в i18n: backend не формулирует
 * пользовательских фраз, поэтому «пробег скручен» не может появиться в принципе.
 * `evidence` — числа, на которых построен вывод; без основания признак не
 * показывается (MVP_PRD §14).
 */
export interface AttentionFlagData {
  code: string;
  severity: AttentionSeverity;
  evidence: Record<string, unknown>;
  rule_version: string;
  calculated_at: string;
}

// --- Происхождение данных (BE-064, MVP_PRD §17) ----------------------------------------

/** Откуда взято значение. Совпадает с `vehicle_field_provenance.source_type`. */
export type ProvenanceSourceType =
  | 'seller_listing'
  | 'catalog_import'
  | 'market_benchmark'
  | 'carviq_calculation'
  | 'editorial_assessment';

/** Формула — только у значений, рассчитанных CarViQ.DE. */
export interface ProvenanceCalculation {
  code: string;
  version: string;
  formula: string | null;
}

export interface ProvenanceEntryData {
  field_name: string;
  source_type: ProvenanceSourceType;
  raw_value: Record<string, unknown> | null;
  normalized_value: Record<string, unknown> | null;
  /** Когда значение получено у источника, ISO 8601. */
  observed_at: string;
  /** Когда значение зафиксировал CarViQ.DE, ISO 8601. */
  recorded_at: string;
  confidence: number;
  calculation: ProvenanceCalculation | null;
  source: VehicleSource | null;
}

export interface VehicleProvenanceData {
  vehicle_id: string;
  entries: ProvenanceEntryData[];
}

/**
 * Закрытая карточка (BE-206, MVP_PRD §34.2).
 *
 * До оплаты открыты ровно шесть значений: марка, модель, год первой
 * регистрации, пробег, обложка и адрес карточки. Всё остальное — цена,
 * продавец, город, модификация, характеристики, Match Score, признаки внимания,
 * сравнение с медианой — **физически отсутствует в ответе API**, а не
 * скрывается на фронте. Поэтому здесь нет и полей под них: нарисовать закрытое
 * значение нечем, и это свойство типа, а не дисциплины разработчика.
 */
export interface VehicleTeaserData {
  access: 'teaser';
  id: string;
  slug: string;
  make: string;
  model: string;
  /** Только год: точная дата первой регистрации закрыта. */
  first_registration_year: number;
  mileage_km: number;
  cover_image: VehicleCoverImage | null;
}

/**
 * Уровень доступа к автомобилю (BE-580, MVP_TECH_RFC §34.11).
 *
 * `teaser` — чужой автомобиль: модель, год, пробег, обложка. `open` —
 * автомобиль рабочего набора своего заказа **до оплаты**: данные объявления
 * открыты, аналитики (Match Score, рынок, признаки внимания, происхождение)
 * в ответе нет физически. `full` — тот же автомобиль после оплаты.
 */
export type VehicleAccess = 'teaser' | 'open' | 'full';

/** Карточка списка: главная (MVP_PRD §6) и публичный каталог (§7). */
export interface VehicleCardData {
  access: 'full';
  id: string;
  slug: string;
  title: string;
  make: string;
  model: string;
  variant: string | null;
  body_type: BodyType;
  price: VehiclePrice;
  /** Дата, `YYYY-MM-DD`. На карточке показывается только год (MVP_PRD §6). */
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  transmission: Transmission;
  /** `null` — источник не указал привод (домысливать его нельзя). */
  drivetrain: Drivetrain | null;
  power_kw: number | null;
  location: VehicleLocation;
  cover_image: VehicleCoverImage | null;
  /** Дата актуальности данных объявления, ISO 8601 UTC. */
  data_observed_at: string;
  /** Первая публикация на платформе CarViQ.DE, ISO 8601 UTC. */
  platform_published_at: string | null;
  /** Опубликовано на CarViQ.DE менее 7 дней назад — бейдж «Новое» (MVP_PRD §2). */
  is_new: boolean;
  match: null;
  /** `null` — аналитика не рассчитывалась (MVP_TECH_RFC §21). */
  market: MarketComparisonData | null;
  /** Число сработавших правил; `null` — аналитика не рассчитывалась. */
  attention_count: number | null;
}

/**
 * Открытая карточка каталога (`VehicleOpenCardOut`, BE-580): автомобиль своего
 * рабочего набора до оплаты. Данные объявления есть, аналитики нет — полей
 * `match`, `market` и `attention_count` здесь не существует.
 */
export interface VehicleOpenCardData {
  access: 'open';
  id: string;
  slug: string;
  title: string;
  make: string;
  model: string;
  variant: string | null;
  body_type: BodyType;
  price: VehiclePrice;
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  transmission: Transmission;
  drivetrain: Drivetrain | null;
  power_kw: number | null;
  location: VehicleLocation;
  cover_image: VehicleCoverImage | null;
  data_observed_at: string;
  platform_published_at: string | null;
  is_new: boolean;
}

/**
 * Карточка каталога в одном из трёх режимов (§34.11).
 *
 * Различаются по `access` — тегу размеченного объединения, а не по «наличию
 * цены»: интерфейс обязан различать ответы по явному признаку, а не по
 * отсутствию поля в JSON.
 */
export type VehicleCatalogItem = VehicleCardData | VehicleOpenCardData | VehicleTeaserData;

/**
 * Что нужно карточке `VehicleCard`, чтобы нарисовать открытое объявление.
 *
 * Структурный тип, а не ещё одна копия ответа: под него подходят и карточка
 * каталога (`full`, `open`), и карточка результата прогона — без
 * переупаковки. Аналитических полей здесь нет: карточка их не рисует, и
 * открытая карточка не может их «потерять».
 */
export interface VehicleCardListing {
  access: 'full' | 'open';
  make: string;
  model: string;
  variant: string | null;
  price: VehiclePrice;
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  transmission: Transmission;
  location: VehicleLocation;
  cover_image: VehicleCoverImage | null;
  data_observed_at: string;
  is_new: boolean;
}

/** Закрытый вид той же карточки: только то, что есть в `teaser`. */
export interface VehicleCardTeaser {
  access: 'teaser';
  make: string;
  model: string;
  first_registration_year: number;
  mileage_km: number;
  cover_image: VehicleCoverImage | null;
}

/** Вход `VehicleCard`: открытое объявление или закрытая карточка. */
export type VehicleCardView = VehicleCardListing | VehicleCardTeaser;

/**
 * Общий формат пагинации (MVP_TECH_RFC §21).
 *
 * **Страница смешанная по определению**: часть автомобилей может входить в
 * оплаченный рабочий набор пользователя, часть — нет.
 */
export interface VehiclePage {
  items: VehicleCatalogItem[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// --- Карточка автомобиля (MVP_PRD §18) ------------------------------------------------

export type VatStatus = 'included' | 'not_included' | 'unknown';
export type ServiceHistoryStatus = 'full' | 'partial' | 'not_provided' | 'unknown';
/**
 * Заявление продавца, а не проверенный факт. Префикс `declared_` обязателен:
 * система не имеет права утверждать «автомобиль не был в аварии» (MVP_PRD §15).
 */
export type AccidentHistoryStatus =
  'declared_accident_free' | 'declared_damage' | 'not_provided' | 'unknown';
export type VinStatus = 'provided' | 'not_provided' | 'unknown';
export type SellerType = 'dealer' | 'private';

export interface VehicleGalleryImage {
  id: string;
  is_cover: boolean;
  large_url: string | null;
  card_url: string | null;
  thumb_url: string | null;
  alt: string | null;
}

/**
 * Характеристики. Поля не пропускаются при отсутствии значения: пропуск создавал
 * бы ложное впечатление полноты, поэтому `null` показывается как «Нет данных».
 */
export interface VehicleSpecs {
  first_registration_date: string;
  mileage_km: number;
  fuel_type: FuelType;
  power_kw: number | null;
  engine_capacity_cc: number | null;
  transmission: Transmission;
  drivetrain: Drivetrain | null;
  body_type: BodyType;
  doors: number | null;
  seats: number | null;
  exterior_color: string | null;
  consumption_l_100km: number | null;
  consumption_kwh_100km: number | null;
  co2_g_km: number | null;
  emission_class: string | null;
  trunk_volume_l: number | null;
}

/** Сведения **из объявления**, а не результат проверки (MVP_PRD §15). */
export interface VehicleHistory {
  owners_count: number | null;
  service_history_status: ServiceHistoryStatus;
  accident_history_status: AccidentHistoryStatus;
  vin_status: VinStatus;
}

/** Продавец — информационный блок. Оценки надёжности продавца в MVP нет. */
export interface VehicleSeller {
  name: string;
  seller_type: SellerType;
  /** `null` — площадка не назвала город автохауса. Показывается как «Нет данных». */
  city: string | null;
  /** Федеральная земля, ISO 3166-2:DE без префикса. `null` — неизвестна. */
  state_code: string | null;
}

export interface VehicleSource {
  name: string;
  url: string | null;
  observed_at: string;
  last_checked_at: string | null;
}

/** Закрытая страница автомобиля (BE-206): тот же набор, что и у карточки. */
export interface VehicleTeaserDetailData {
  access: 'teaser';
  id: string;
  slug: string;
  make: string;
  model: string;
  first_registration_year: number;
  mileage_km: number;
  cover_image: VehicleCoverImage | null;
}

export interface VehicleDetailData {
  access: 'full';
  id: string;
  slug: string;
  status: string;
  title: string;
  make: string;
  model: string;
  generation: string | null;
  variant: string | null;
  price: VehiclePrice;
  vat_status: VatStatus;
  negotiable: boolean | null;
  location: VehicleLocation;
  description: string;
  specs: VehicleSpecs;
  history: VehicleHistory;
  /** Коды справочника оснащения; тексты — через i18n. */
  feature_codes: string[];
  images: VehicleGalleryImage[];
  seller: VehicleSeller | null;
  /**
   * Объявление на сайте автохауса — только `http`/`https` (BE-580, SEC-580).
   * `null` — ссылки нет либо она недопустима.
   */
  listing_url: string | null;
  source: VehicleSource | null;
  data_observed_at: string;
  platform_published_at: string | null;
  is_new: boolean;
  match: null;
  market: MarketComparisonData | null;
  /** `null` — правила не запускались, `[]` — ни одно не сработало. Это разные вещи. */
  attention_flags: AttentionFlagData[] | null;
}

/**
 * Открытая страница автомобиля (`VehicleOpenDetailOut`, BE-580): свой
 * автомобиль рабочего набора до оплаты.
 *
 * Всё объявление — цена, характеристики, описание, галерея, автохаус и ссылка
 * на объявление — открыто. Аналитики (`market`, `attention_flags`, `match`) и
 * происхождения данных (`source`) в ответе **нет физически**, поэтому нет и
 * полей под них: нарисовать их нечем. E-mail автохауса не приходит ни на одном
 * уровне.
 */
export interface VehicleOpenDetailData {
  access: 'open';
  id: string;
  slug: string;
  status: string;
  title: string;
  make: string;
  model: string;
  generation: string | null;
  variant: string | null;
  price: VehiclePrice;
  vat_status: VatStatus;
  negotiable: boolean | null;
  location: VehicleLocation;
  description: string;
  specs: VehicleSpecs;
  history: VehicleHistory;
  feature_codes: string[];
  images: VehicleGalleryImage[];
  seller: VehicleSeller | null;
  /** Только `http`/`https`; `null` — ссылки нет либо она недопустима. */
  listing_url: string | null;
  data_observed_at: string;
  platform_published_at: string | null;
  is_new: boolean;
}

/** Страница автомобиля в одном из трёх режимов (§34.11). */
export type VehicleDetailItem = VehicleDetailData | VehicleOpenDetailData | VehicleTeaserDetailData;

/** Закрытая ли карточка. Одна проверка на весь фронт — вместо `'price' in item`. */
export function isTeaser(
  item: VehicleCardView | VehicleCatalogItem | VehicleDetailItem,
): item is VehicleCardTeaser {
  return item.access === 'teaser';
}
